import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Icon from '@mui/material/Icon';
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import Backdrop from '@mui/material/Backdrop';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';

import { navbar, navbarContainer } from 'examples/Navbars/DashboardNavbar/styles';

import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from 'context';

import { useMe } from 'core/me/provider/MeProvider';
import { tokenStorage } from 'shared/api/storage';

function isSmallScreen() {
  return window.innerWidth < 1200;
}

function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const navigate = useNavigate();
  const theme = useMuiTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { me } = useMe();

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;

  // menus
  const [openNotifEl, setOpenNotifEl] = useState(null);
  const [openUserEl, setOpenUserEl] = useState(null);

  // mobile “application menu” (tailwind’deki üç nokta)
  const [appMenuOpen, setAppMenuOpen] = useState(false);

  // notification ping
  const [notifying, setNotifying] = useState(true);

  const [mobile, setMobile] = useState(() => isSmallScreen());

  // Smart search
  const searchRef = useRef(null);
  const [q, setQ] = useState('');

  const options = useMemo(
    () => [
      { label: 'Üyeler', to: '/app/pilates/members' },
      { label: 'Takvim', to: '/app/pilates/sessions' },
      { label: 'Seans Ekle', to: '/app/pilates/sessions?new=1' },
      { label: 'Ödeme Al', to: '/app/pilates/payments?new=1' }, // bu route gerçekten var mı emin ol
      { label: 'Çalışanlar', to: '/app/pilates/staff' },
      { label: 'Paketler', to: '/app/pilates/packages' },
    ],
    []
  );

  const brandTitleRaw = (me?.companyName || '').trim() || 'Solisup';
  const brandTitle = brandTitleRaw.toLocaleUpperCase('tr-TR');

  // Navbar transparency (scroll)
  useEffect(() => {
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }
    window.addEventListener('scroll', handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener('scroll', handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Large screen: sidebar hep açık + hamburger yok
  useEffect(() => {
    function onResize() {
      const m = isSmallScreen();
      setMobile(m);
      if (!m && miniSidenav) setMiniSidenav(dispatch, false);
      if (!m) setAppMenuOpen(false); // desktop’a çıkınca mobile menu kapat
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [dispatch, miniSidenav]);

  // Ctrl/⌘ + K focus search
  useEffect(() => {
    function onKeyDown(e) {
      const isK = (e.key || '').toLowerCase() === 'k';
      if ((e.ctrlKey || e.metaKey) && isK) {
        e.preventDefault();
        const input = searchRef.current?.querySelector('input');
        input?.focus();
      }
      if (e.key === 'Escape') {
        setAppMenuOpen(false);
        setOpenNotifEl(null);
        setOpenUserEl(null);
        if (mobile && !miniSidenav) setMiniSidenav(dispatch, true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatch, mobile, miniSidenav]);

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      return colorValue;
    },
  });

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const handleMobileSidenav = () => {
    if (!mobile) return;
    setMiniSidenav(dispatch, !miniSidenav);
  };

  const searchPlaceholder = mobile
    ? 'Ara… (Ctrl/⌘K)'
    : 'Akıllı arama: üye, takvim, ödeme… (Ctrl/⌘K)';

  // ✅ burayı senin auth yapına bağla
  async function logout() {
    tokenStorage.clear?.();
    window.location.href = '/sign-in'; // veya /
  }

  return (
    <>
      {mobile && !miniSidenav ? (
        <Backdrop
          open
          onClick={() => setMiniSidenav(dispatch, true)}
          sx={{
            zIndex: (t) => t.zIndex.drawer - 1,
            backgroundColor: darkMode ? 'rgba(0,0,0,0.35)' : 'rgba(17,17,17,0.18)',
          }}
        />
      ) : null}

      <AppBar
        position={absolute ? 'absolute' : fixedNavbar ? 'sticky' : 'static'}
        color="inherit"
        sx={(t) => navbar(t, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar
          sx={(t) => ({
            ...navbarContainer(t),
            minHeight: '56px !important',
            py: 0.5,
            px: { xs: 1, sm: 1.5, md: 2 },
          })}
        >
          <MDBox
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              minWidth: 0,
            }}
          >
            {/* LEFT */}
            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {mobile ? (
                <IconButton
                  size="small"
                  disableRipple
                  sx={{ p: 0.75 }}
                  onClick={handleMobileSidenav}
                >
                  <Icon sx={iconsStyle} fontSize="medium">
                    {miniSidenav ? 'menu' : 'close'}
                  </Icon>
                </IconButton>
              ) : null}

              <MDBox
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <MDTypography
                  variant="button"
                  fontWeight={800}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { sm: 200, md: 280 },
                    letterSpacing: '0.08em',
                    fontSize: '0.8rem',
                  }}
                  title={brandTitle}
                >
                  {brandTitle}
                </MDTypography>

                {me?.plan === 'TRIAL' && (
                  <MDBox
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: '999px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      backgroundColor: 'warning.light',
                      color: 'warning.dark',
                    }}
                  >
                    TRIAL
                  </MDBox>
                )}
              </MDBox>
            </MDBox>

            {/* CENTER */}
            {isMini ? (
              <MDBox />
            ) : (
              <>
                {/* ✅ Desktop: Search */}
                <MDBox
                  ref={searchRef}
                  sx={{
                    display: { xs: 'none', sm: 'block' }, // xs'de gizle
                    justifySelf: 'center',
                    width: '100%',
                    maxWidth: { sm: 520, md: 680 },
                    minWidth: 0,
                  }}
                >
                  <Autocomplete
                    options={options}
                    size="small"
                    freeSolo
                    clearOnEscape
                    value={null}
                    inputValue={q}
                    onInputChange={(e, v) => setQ(v)}
                    onChange={(e, opt) => {
                      if (opt?.to) navigate(opt.to);
                    }}
                    PaperComponent={(props) => (
                      <Paper
                        {...props}
                        elevation={10}
                        sx={{
                          borderRadius: '14px',
                          overflow: 'hidden',
                          border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      />
                    )}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        placeholder="Akıllı arama… (Ctrl/⌘K)"
                        label={null}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Icon fontSize="small" sx={{ opacity: 0.75 }}>
                                search
                              </Icon>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </MDBox>

                {/* ✅ Mobile: Company Name */}
                <MDBox
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <MDTypography
                    variant="button"
                    fontWeight={800}
                    sx={{
                      letterSpacing: '0.08em',
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '60vw',
                    }}
                    title={brandTitle}
                  >
                    {brandTitle}
                  </MDTypography>
                </MDBox>
              </>
            )}

            {/* RIGHT */}
            <MDBox
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}
            >
              {/* desktop right actions OR mobile expanded actions */}
              <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {/* Settings */}
                <IconButton
                  size="small"
                  disableRipple
                  sx={{ px: 0.5, display: { xs: 'none', sm: 'inline-flex' } }}
                  onClick={handleConfiguratorOpen}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>

                {/* Notifications */}
                <IconButton
                  size="small"
                  disableRipple
                  sx={{ px: 0.5 }}
                  onClick={(e) => {
                    setOpenNotifEl(e.currentTarget);
                    setNotifying(false);
                  }}
                >
                  <Badge
                    variant="dot"
                    color="warning"
                    invisible={!notifying}
                    sx={{
                      '& .MuiBadge-badge': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.background.paper, 0.9)}`,
                      },
                    }}
                  >
                    <Icon sx={iconsStyle}>notifications</Icon>
                  </Badge>
                </IconButton>

                {/* User */}
                <IconButton
                  size="small"
                  disableRipple
                  sx={{ px: 0.5 }}
                  onClick={(e) => setOpenUserEl(e.currentTarget)}
                >
                  <Avatar
                    src="/images/user/owner.jpg"
                    sx={{
                      width: 30,
                      height: 30,
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                    }}
                  />
                </IconButton>
              </MDBox>

              {/* Notification Menu */}
              <Menu
                anchorEl={openNotifEl}
                open={Boolean(openNotifEl)}
                onClose={() => setOpenNotifEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 1.25 }}
                PaperProps={{
                  sx: { borderRadius: '14px', minWidth: 280, overflow: 'hidden' },
                }}
              >
                <MDBox sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <MDTypography variant="button" fontWeight="bold">
                    Bildirimler
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    Son aktiviteler
                  </MDTypography>
                </MDBox>
                <Divider />
                <MenuItem onClick={() => setOpenNotifEl(null)}>Yeni mesajlar</MenuItem>
                <MenuItem onClick={() => setOpenNotifEl(null)}>Bugünkü seanslar</MenuItem>
                <MenuItem onClick={() => setOpenNotifEl(null)}>Bekleyen tahsilatlar</MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setOpenNotifEl(null);
                    navigate('/notifications');
                  }}
                >
                  Tümünü gör
                </MenuItem>
              </Menu>

              {/* User Menu */}
              <Menu
                anchorEl={openUserEl}
                open={Boolean(openUserEl)}
                onClose={() => setOpenUserEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: 1.25 }}
                PaperProps={{
                  sx: { borderRadius: '14px', minWidth: 260, overflow: 'hidden' },
                }}
              >
                <MDBox sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <MDTypography variant="button" fontWeight="bold">
                    {(me?.fullName || 'Kullanıcı').trim()}
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    {me?.email || ''}
                  </MDTypography>
                </MDBox>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setOpenUserEl(null);
                    navigate('/app/pilates/profile');
                  }}
                >
                  Profil
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setOpenUserEl(null);

                    const phoneE164 = '905421162306';
                    const text = encodeURIComponent(
                      'Merhaba, SolisUp ile ilgili destek almak istiyorum.'
                    );

                    window.open(
                      `https://wa.me/${phoneE164}?text=${text}`,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >
                  Destek
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={async () => {
                    setOpenUserEl(null);
                    await logout();
                  }}
                >
                  Çıkış Yap
                </MenuItem>
              </Menu>
            </MDBox>
          </MDBox>
        </Toolbar>
      </AppBar>
    </>
  );
}

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
